import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { QUERY_CACHE_TIME_MS } from '@/config/constants';
import type { ChartDataRange } from '@/features/charts/types/chartData';
import type { ChartType } from '@/features/charts/types/highcharts';
import type { TemplateChartSeries } from '@/shared/types/charts';

import { chartsApi } from './charts.api';

const buildSeriesKey = (series: TemplateChartSeries[]) =>
  series.map(({ symbol }) => {
    const key = symbol.key;
    return {
      group: symbol.group,
      code: key.Code,
      periodType: key.PeriodType,
      period: key.Period,
      timeRef: key.TimeRef,
      transactionDate: symbol.transactionDate ?? key.transactionDate,
      overrideTime: symbol.overrideTime,
      from: symbol.from,
      to: symbol.to
    };
  });

const seriesKey = (series?: TemplateChartSeries[]) => buildSeriesKey(series ?? []);

export const chartOptions = {
  all: () => ['charts'] as const,

  data: (chartType: ChartType, templateChartSeries?: TemplateChartSeries[], timezone = 'UTC') =>
    queryOptions({
      queryKey: [
        ...chartOptions.all(),
        'data',
        chartType,
        seriesKey(templateChartSeries),
        timezone
      ] as const,
      queryFn: () => chartsApi.getChartData(templateChartSeries ?? [], chartType, timezone),
      enabled: templateChartSeries !== undefined,
      staleTime: QUERY_CACHE_TIME_MS,
      gcTime: QUERY_CACHE_TIME_MS,
      placeholderData: keepPreviousData
    }),

  seasonal: (templateChartSeries?: TemplateChartSeries[], chartDataRange?: ChartDataRange) => {
    const firstSeries = templateChartSeries?.[0];
    const range = chartDataRange;
    return queryOptions({
      queryKey: [
        ...chartOptions.all(),
        'seasonal',
        seriesKey(firstSeries ? [firstSeries] : []),
        range?.from,
        range?.to,
        firstSeries ?? null,
        range ?? null
      ] as const,
      queryFn: () => {
        if (!firstSeries || !range) return Promise.resolve(null);
        return chartsApi.getSeasonalChartData(firstSeries, range);
      },
      enabled: !!firstSeries && !!range?.from,
      staleTime: QUERY_CACHE_TIME_MS,
      gcTime: QUERY_CACHE_TIME_MS,
      placeholderData: keepPreviousData
    });
  },

  custom: () => [...chartOptions.all(), 'custom'] as const,

  customConfig: (fileName?: string, groupName?: string) =>
    queryOptions({
      queryKey: [...chartOptions.custom(), 'config', groupName ?? null, fileName ?? null] as const,
      queryFn: () => chartsApi.getCustomChartConfig(fileName, groupName),
      enabled: Boolean(fileName && groupName),
      refetchOnWindowFocus: false,
      gcTime: Infinity,
      staleTime: Infinity
    }),

  customData: ({
    fileName,
    groupName,
    queryFileId,
    from,
    to
  }: {
    fileName?: string;
    groupName?: string;
    queryFileId?: string;
    from?: string;
    to?: string;
  }) =>
    queryOptions({
      queryKey: [
        ...chartOptions.custom(),
        'data',
        groupName ?? null,
        queryFileId ?? null,
        fileName ?? null,
        from ?? null,
        to ?? null
      ] as const,
      queryFn: () => chartsApi.getCustomChartData({ fileName, groupName, queryFileId, from, to }),
      enabled: Boolean(queryFileId || (fileName && groupName)),
      refetchOnWindowFocus: false,
      gcTime: Infinity,
      staleTime: Infinity
    })
};
